import {
  hasPermission,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_CHANNELS,
  type CreateCampaignInput,
  type MessageChannel,
} from '@crm/shared'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, Field } from '@/components/ui/field'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { MultiSelect } from '@/components/ui/multi-select'
import { Select, Textarea } from '@/components/ui/select'
import { countryOptions, jobCategoryOptions, statusOptions } from '@/features/clients/options'
import {
  templatesQuery,
  useCreateCampaign,
  usePreviewCampaign,
  type CampaignPreview,
} from '@/features/messaging/api'
import { ApiClientError } from '@/lib/api'
import { fetchMe } from '@/lib/auth-query'

export const Route = createFileRoute('/_app/campaigns/new')({
  beforeLoad: async ({ context }) => {
    const me = await fetchMe(context.queryClient)
    if (me && !hasPermission(me.user.role, 'campaigns:send')) {
      throw redirect({ to: '/campaigns' })
    }
  },
  component: NewCampaignPage,
})

type Filter = {
  status?: string[]
  country?: string[]
  category?: string[]
}

/**
 * The campaign wizard (PLAN §7.3): name & channel → audience filter → live
 * preview → send/schedule. The preview count is the load-bearing part — nobody
 * should fire a blast without seeing exactly who it hits first.
 */
function NewCampaignPage() {
  const navigate = useNavigate()
  const templates = useQuery(templatesQuery)
  const preview = usePreviewCampaign()
  const create = useCreateCampaign()

  const [step, setStep] = useState<0 | 1 | 2>(0)
  const [name, setName] = useState('')
  const [channel, setChannel] = useState<MessageChannel>('sms')
  const [templateId, setTemplateId] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [filter, setFilter] = useState<Filter>({ status: ['confirmed', 'active'] })
  const [scheduledAt, setScheduledAt] = useState('')
  const [previewData, setPreviewData] = useState<CampaignPreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const channelTemplates = templates.data?.items.filter((t) => t.channel === channel) ?? []

  // Refresh the preview whenever the audience or channel changes on the preview step.
  useEffect(() => {
    if (step !== 1) return
    const timer = setTimeout(() => {
      preview.mutate(
        { channel, filter: filter as Record<string, unknown> },
        { onSuccess: setPreviewData },
      )
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, channel, JSON.stringify(filter)])

  function applyTemplate(id: string) {
    setTemplateId(id)
    const template = templates.data?.items.find((t) => t.id === id)
    if (template) {
      setBody(template.body)
      if (template.subject) setSubject(template.subject)
    }
  }

  function submit(schedule: boolean) {
    setError(null)
    create.mutate(
      {
        name,
        channel,
        templateId: templateId || undefined,
        subject: channel === 'email' ? subject : undefined,
        body: body || undefined,
        // The server re-validates against clientFilterSchema; this UI filter is a
        // loose subset of it, so the cast is safe.
        filter: filter as CreateCampaignInput['filter'],
        scheduledAt: schedule && scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
      },
      {
        onSuccess: (data) =>
          navigate({ to: '/campaigns/$campaignId', params: { campaignId: data.campaign.id } }),
        onError: (e) =>
          setError(e instanceof ApiClientError ? e.message : 'Could not create the campaign'),
      },
    )
  }

  const canContinue = step === 0 ? name.trim() && (templateId || body.trim()) : true

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to campaigns
        </Link>
        <h1 className="mt-1 text-2xl font-semibold">New campaign</h1>
      </div>

      <div className="flex gap-1.5">
        {['Message', 'Audience', 'Review'].map((label, i) => (
          <div
            key={label}
            className={cn(
              'h-1 flex-1 rounded-full',
              i <= step ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]',
            )}
          />
        ))}
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
        >
          {error}
        </div>
      )}

      {step === 0 && (
        <Card className="space-y-4 p-4">
          <Field label="Campaign name" htmlFor="name" required>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>

          <Field label="Channel" htmlFor="channel" required>
            <Select
              id="channel"
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value as MessageChannel)
                setTemplateId('')
              }}
            >
              {MESSAGE_CHANNELS.map((ch) => (
                <option key={ch} value={ch}>
                  {MESSAGE_CHANNEL_LABELS[ch]}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Template" htmlFor="template" hint="Or write a one-off message below.">
            <Select
              id="template"
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
            >
              <option value="">— None —</option>
              {channelTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>

          {channel === 'email' && (
            <Field label="Subject" htmlFor="subject">
              <Input id="subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Field>
          )}

          <Field
            label="Message"
            htmlFor="body"
            hint="Placeholders like {{first_name}} are filled per client."
          >
            <Textarea id="body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
          </Field>
        </Card>
      )}

      {step === 1 && (
        <Card className="space-y-4 p-4">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Only contactable clients who consent to {MESSAGE_CHANNEL_LABELS[channel]} are included —
            consent is enforced, never optional.
          </p>

          <Field label="Status" htmlFor="f-status">
            <MultiSelect
              id="f-status"
              options={statusOptions}
              value={filter.status ?? []}
              onChange={(v) => setFilter((f) => ({ ...f, status: v.length ? v : undefined }))}
              placeholder="Any status"
            />
          </Field>

          <Field label="Preferred countries" htmlFor="f-country">
            <MultiSelect
              id="f-country"
              options={countryOptions}
              value={filter.country ?? []}
              onChange={(v) => setFilter((f) => ({ ...f, country: v.length ? v : undefined }))}
              placeholder="Any country"
            />
          </Field>

          <Field label="Job categories" htmlFor="f-category">
            <MultiSelect
              id="f-category"
              options={jobCategoryOptions}
              value={filter.category ?? []}
              onChange={(v) => setFilter((f) => ({ ...f, category: v.length ? v : undefined }))}
              placeholder="Any category"
            />
          </Field>

          <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[var(--color-primary)]" />
              <span className="text-sm font-medium">
                {preview.isPending
                  ? 'Counting…'
                  : `${(previewData?.total ?? 0).toLocaleString()} recipient${previewData?.total === 1 ? '' : 's'}`}
              </span>
            </div>
            {previewData && previewData.sample.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-xs text-[var(--color-muted-foreground)]">
                {previewData.sample.slice(0, 10).map((s) => (
                  <li key={s.id}>
                    {s.fullName} · {s.phone}
                  </li>
                ))}
                {previewData.total > previewData.sample.length && (
                  <li>
                    …and {(previewData.total - previewData.sample.length).toLocaleString()} more
                  </li>
                )}
              </ul>
            )}
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="space-y-4 p-4">
          <h2 className="text-sm font-semibold">Review &amp; send</h2>
          <dl className="space-y-2 text-sm">
            <Row label="Name" value={name} />
            <Row label="Channel" value={MESSAGE_CHANNEL_LABELS[channel]} />
            <Row label="Recipients" value={(previewData?.total ?? 0).toLocaleString()} />
          </dl>

          <div className="rounded-md bg-[var(--color-muted)] p-3 text-sm">
            <p className="whitespace-pre-wrap">{body}</p>
          </div>

          <Field
            label="Schedule for later (optional)"
            htmlFor="scheduledAt"
            hint="Leave blank to send now."
          >
            <Input
              id="scheduledAt"
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
          </Field>

          {(previewData?.total ?? 0) === 0 && (
            <p className="text-sm text-[var(--color-destructive)]">
              No recipients match — go back and widen the audience.
            </p>
          )}
        </Card>
      )}

      <div className="flex justify-between">
        <Button
          variant="ghost"
          onClick={() => setStep((s) => Math.max(0, s - 1) as 0 | 1 | 2)}
          disabled={step === 0}
        >
          Back
        </Button>

        {step < 2 ? (
          <Button onClick={() => setStep((s) => (s + 1) as 0 | 1 | 2)} disabled={!canContinue}>
            Continue
          </Button>
        ) : (
          <div className="flex gap-2">
            {scheduledAt && (
              <Button
                variant="outline"
                loading={create.isPending}
                disabled={(previewData?.total ?? 0) === 0}
                onClick={() => submit(true)}
              >
                Schedule
              </Button>
            )}
            <Button
              loading={create.isPending}
              disabled={(previewData?.total ?? 0) === 0 || Boolean(scheduledAt)}
              onClick={() => submit(false)}
            >
              Send now
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[var(--color-muted-foreground)]">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
